import { NgClass } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DomSanitizer } from '@angular/platform-browser';
import { Events } from '@app/core/models/event-map';
import { SalesCompetitionData } from '@app/core/models/sales-competition.model';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { MarketingNotificationComponent } from '@app/shared/components/modals/marketing-notification/marketing-notification.component';
import { MarketingPopupComponent } from '@app/shared/components/modals/marketing-popup/marketing-popup.component';
import { MarketingNotifications } from '@app/shared/models/notifications.model';
import { accessLevel } from '@app/shared/models/role-enum';
import { DatawareApiService } from '@app/shared/services/dw-api.service';
import { PortalApiService } from '@app/shared/services/portal-api.service';
import { UserService } from '@app/shared/services/User.Service';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';


@Component({
    selector: 'kp-content',
    imports: [NgClass, MarketingNotificationComponent, MarketingPopupComponent, ModalModule],
    templateUrl: './content.component.html',
    styleUrl: './content.component.scss'
})
export class ContentComponent implements OnInit {

    @ViewChild('marketingNotificationTemplate', { static: true }) private marketingNotificationTemplate!: TemplateRef<void>;
    readonly marketingModalId: string = 'marketingNotificationModalId'

    @ViewChild('marketingPopupTemplate', { static: true }) private marketingPopupTemplate!: TemplateRef<void>;
    readonly marketingPopupModalId: string = 'marketingPopupTemplateId'

    #destroyRef = inject(DestroyRef)
    #subscriptions: Subscription[] = []
    #publishSubscribeService = inject(PublishSubscribeService)
    #store = inject(Store)
    #datawareApiService = inject(DatawareApiService)
    #userInternalService = inject(UserService)
    #portalApiService = inject(PortalApiService)
    #sanitizer = inject(DomSanitizer)
    #modalService = inject(BsModalService)

    private permissions = this.#store.selectSignal(UserContextSelectors.hasPermission('Aftermarket', 'aftermarket_role'))

    private isAuthenticated = this.#store.selectSignal(UserContextSelectors.isAuthenticated)

    private accessRole = computed(() => {
        const aftermarketAccess = this.permissions()
        return aftermarketAccess?.value ?? null
    })

    private userRole = this.#store.selectSignal(UserContextSelectors.userRole)
    private notificationShown: number[] = []

    isTechnician = computed(() => {
        const role = this.userRole()
        const accessRole = this.accessRole()
        const isAuthenticated = this.isAuthenticated()

        return isAuthenticated && role == accessLevel.AFTERMARKET_TECHNICIAN && accessRole === 'TECHNICIAN'
    })

    isAftermarket = computed(() => {
        const accessRole: string = this.accessRole()
        const userRole: number = this.userRole()
        return ['MANAGER', 'TECHNICIAN', 'ADMIN'].includes(accessRole) || userRole === accessLevel.AFTERMARKET_TECHNICIAN
    })

    showNotificationsBanner = signal<boolean>(true)
    notificationsBannerStr = signal<string>('')
    salesCompetitionData = signal<SalesCompetitionData | null>(null)
    html: string
    note: any
    popup: any

    ngOnInit(): void {

        this.#subscriptions.push(
            this.#publishSubscribeService.on(Events.UserService.USER_LOGGED_IN).pipe(
                takeUntilDestroyed(this.#destroyRef)
            ).subscribe({
                next: () => {
                    this.getSalesCompetitionText()
                }
            })
        )

        this.getSalesCompetitionText()
    }

    // Get Entities from dataservice/Factory
    private getSalesCompetitionText() {
        if (this.isAftermarket()) {
            return
        }

        this.salesCompetitionData.set(null)
        const portalUserLoggedIn = this.#userInternalService.getPortalUser_LoggedIn()

        if (!portalUserLoggedIn || !environment.dwAvailable) return

        this.#datawareApiService.getSalesCompetitionResult(portalUserLoggedIn.SVFACCOUNT, portalUserLoggedIn.NETWORKALIAS)
            .pipe(
                takeUntilDestroyed(this.#destroyRef)
            ).subscribe({
                next: (axResponse) => {
                    if (axResponse?.Data) {
                        this.salesCompetitionData.set(axResponse.Data);
                        this.checkMarketingNotifications(this.salesCompetitionData().IsActive);
                    }
                },
                error: (err) => {
                    console.error('getSalesCompetitionResult', `${err?.status}-${err?.statusText}, ${err?.message ?? JSON.stringify(err)}`)
                }
            })
    }

    protected openSalesCompetition() {
        if (this.salesCompetitionData()?.Url && this.salesCompetitionData()?.Hash) {
            const url = this.salesCompetitionData().Url;
            const hash = this.salesCompetitionData().Hash;
            window.open("" + url + encodeURI(hash), "_blank");
        }
    }

    protected trustAsHtml(str: string) {
        return this.#sanitizer.bypassSecurityTrustHtml(str)
    }

    // Get new MarketingNotifications from dataservice/Factory
    private checkMarketingNotifications(isSalesCompetitionActive: boolean) {
        this.#portalApiService.getMarketingNotifications(isSalesCompetitionActive)
            .subscribe({
                next: (response) => {
                    this.postMarketingNotification(response)
                }
            })
    }

    // Process marketing notification
    private postMarketingNotification(result: MarketingNotifications) {
        let lastSeenNotificationId: number = 0

        if (result.AllOk) {
            const notificationsBanner = result.notificationsBanner;
            const notificationsModal = result.notificationsModal;
            const notificationsPopup = result.notificationsPopup;
            this.notificationsBannerStr.set('');

            //BANNER
            for (let i: number = 0; i < notificationsBanner.length; i++) {
                const notification = notificationsBanner[i];
                let delayBanner = notification.SECONDSTODISPLAYPOPUP;
                const height = notification.CONTENTHEIGHT == 0 ? '100%' : (notification.CONTENTHEIGHT + 'px');
                const width = notification.CONTENTWIDTH == 0 ? '100%' : (notification.CONTENTWIDTH + 'px');
                let htmlSource = '<iframe src="' + notification.CONTENTURL + '" style="height: ' + height + ';border-width: 0;width: ' + width + ';display: inline-block; margin-right:2px;"';
                htmlSource += 'class="iframe" allowtransparency="true" frameborder="0" scrolling="no" allowfullscreen></iframe>';
                this.notificationsBannerStr.update(html => html + htmlSource)
                this.showNotificationsBanner.set(true);

                if (delayBanner > 0) {
                    delayBanner = delayBanner * 1000;

                    setTimeout(() => {
                        this.showNotificationsBanner.set(false)
                    }, delayBanner)
                }
            }

            //MODAL
            for (let i: number = 0; i < notificationsModal.length; i++) {
                const notification = notificationsModal[i]

                if (this.notificationShown.indexOf(notification.ID) >= 0) {
                    continue
                }

                this.notificationShown.push(notification.ID)
                const height: string = notification.CONTENTHEIGHT == 0 ? '100%' : (notification.CONTENTHEIGHT + 'px')
                const width: string = notification.CONTENTWIDTH == 0 ? '100%' : (notification.CONTENTWIDTH + 'px')
                const modalSize: string = notification.CONTENTWIDTH == 0 || notification.CONTENTWIDTH > 550 ? 'modal-lg' : 'modal-md'
                let htmlSource: string = '<iframe src="' + notification.CONTENTURL + '" style="height: ' + height + ';border-width: 0;width: ' + width + ';display: block; margin:auto;"'
                htmlSource += 'class="iframe" allowtransparency="true" frameborder="0" scrolling="auto" allowfullscreen></iframe>'
                this.openModalMarketingNotification(notification, htmlSource, modalSize)
                lastSeenNotificationId = notification.ID > lastSeenNotificationId ? notification.ID : lastSeenNotificationId
            }

            //NOTIFICATION POPUP
            for (let i: number = 0; i < notificationsPopup.length; i++) {
                const notification = notificationsPopup[i]

                if (this.notificationShown.indexOf(notification.ID) >= 0) {
                    continue
                }

                this.notificationShown.push(notification.ID)

                this.openModalMarketingPopup(notification)

                lastSeenNotificationId = notification.ID > lastSeenNotificationId ? notification.ID : lastSeenNotificationId
            }

            if (lastSeenNotificationId > 0) {
                this.axPortal_SetLastSeenMarketingNotification(lastSeenNotificationId)
            }
        }
    }

    // Set last seen marketing notificationId on PortalUser in AX
    private axPortal_SetLastSeenMarketingNotification(lastSeenNotificationId: number) {
        this.#portalApiService.axPortal_SetLastSeenMarketingNotification(lastSeenNotificationId)
            .subscribe()
    }

    private openModalMarketingNotification(notification, html, size) {

        this.note = notification
        this.html = html

        this.#modalService.show(this.marketingNotificationTemplate, { id: this.marketingModalId, class: size })
    }

    private openModalMarketingPopup(popup: any): void {

        this.popup = popup

        this.#modalService.show(this.marketingPopupTemplate, { id: this.marketingPopupModalId, class: 'modal-dialog-centered modal-lg' })
    }
}
